import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useNavigate } from 'react-router-dom'
import { RiLoginCircleFill, RiEyeFill, RiEyeOffFill } from "react-icons/ri";
import { BsBuildingsFill } from "react-icons/bs";
import { FaArrowLeft } from "react-icons/fa";
import utils from '../utils'
import { useApp } from '../context/AppContext'
import authServices from '../services/AuthServices';

function LoginPage() {
    const navigate = useNavigate()
    const { setCredentials, setUser } = useApp()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')

    function submitHandler(e) {
        e.preventDefault()
        setError('')
        setIsLoading(true)

        if (!email || !password) {
            setError('Please enter both email and password')
            setIsLoading(false)
            return
        }

        authServices.login({ email, password })
            .then(res => {
                const { access, refresh } = res.data
                setCredentials({ access, refresh })
                authServices.getUser().then(
                    res => {
                        setUser(res.data)
                        navigate('/auth/dashboard')
                    },
                    err => {
                        setError('Failed to retrieve user details after login. Please try again.')
                        console.error('Get user error:', err)
                    }
                )
            })
            .catch(err => {
                const msg = err.response?.data?.detail
                    || err.response?.data?.non_field_errors?.[0]
                    || 'Login failed. Please check your credentials.'
                setError(msg)
                console.error('Login error:', err)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    return (
        <div>
            <Helmet>
                <title>Admin Login - {utils.siteName}</title>
            </Helmet>

            <div className="mt-16">
                <div className="mb-12 flex justify-center">
                    <Link
                        to={'/'}
                        className="group flex items-center gap-2 text-on-surface-variant hover:text-primary transition-colors duration-300"
                    >
                        <FaArrowLeft className="text-sm" />
                        <span className="text-[10px] font-semibold tracking-[0.2em] uppercase">
                            Back to Site
                        </span>
                    </Link>
                </div>

                <div className="flex items-center justify-center p-6 selection:bg-secondary/20 selection:text-secondary">
                    <div className="bg-surface-container-lowest rounded-xl p-10 md:p-14 border border-outline-variant/15 relative overflow-hidden group max-w-lg w-full">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/5 via-secondary/20 to-primary/5"></div>

                        <div className="flex flex-col items-center mb-12 text-center">
                            <div className="mb-8 p-4 bg-surface-container-low rounded-full">
                                <BsBuildingsFill className="text-4xl text-primary" />
                            </div>
                            <span className="text-[11px] font-medium tracking-[0.25em] uppercase text-on-surface-variant mb-3 block">
                                Management Suite
                            </span>
                        </div>

                        <form className="space-y-10" onSubmit={submitHandler}>
                            <div className="relative">
                                <label
                                    className="absolute -top-6 left-0 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant"
                                    htmlFor="email"
                                >
                                    Admin *
                                </label>
                                <input
                                    className="w-full bg-transparent border-b border-outline-variant/40 py-3 px-0 focus:outline-none focus:border-primary transition-all duration-300 text-on-surface placeholder:text-outline-variant/60"
                                    id="email"
                                    name="email"
                                    placeholder="e.g. user@sage.com"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isLoading}
                                    autoComplete="username"
                                />
                            </div>

                            <div className="relative">
                                <label
                                    className="absolute -top-6 left-0 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant"
                                    htmlFor="password"
                                >
                                    Password *
                                </label>
                                <div className="relative">
                                    <input
                                        className="w-full bg-transparent border-b border-outline-variant/40 py-3 px-0 pr-10 focus:outline-none focus:border-primary transition-all duration-300 text-on-surface placeholder:text-outline-variant/60"
                                        id="password"
                                        name="password"
                                        placeholder="••••••••"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={isLoading}
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-0 top-1/2 -translate-y-1/2 text-on-surface-variant/60 hover:text-primary transition-colors p-1"
                                        tabIndex="-1"
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? (
                                            <RiEyeOffFill className="text-lg" />
                                        ) : (
                                            <RiEyeFill className="text-lg" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 bg-error/10 border border-error/30 rounded-lg text-xs text-error">
                                    {error}
                                </div>
                            )}
                            <div className="pt-4">
                                <button
                                    className="w-full bg-secondary text-on-secondary py-4 px-6 rounded-xl font-semibold tracking-wider text-sm hover:opacity-90 hover:scale-[1.01] transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                                    type="submit"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="animate-spin">⏳</span>
                                            Signing in...
                                        </>
                                    ) : (
                                        <>
                                            Enter Workspace
                                            <RiLoginCircleFill className="text-lg" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default LoginPage